import {
  addDoc,
  collection,
  doc,
  getCountFromServer,
  getDoc,
  query,
  Timestamp,
  where,
} from "firebase/firestore";
import { useCallback, useEffect, useRef, useState } from "react";
import { db } from "../firebase";
import { useCaptureReferralFromUrl } from "../hooks/referral/useCaptureReferralFromUrl";
import {
  assignExperimentGroup,
  EXPERIMENT,
} from "../services/analyticsServices";
import { readStoredAdAttribution } from "../services/adAttribution";
import callCloudRunFunction from "../services/cloudRunServices";
import {
  addCrashBreadcrumb,
  setSessionContext as setCrashSessionContext,
} from "../services/clientCrashReporter";
import {
  getActiveEmployeesAtLocation,
  normalizeManualLocationData,
} from "../services/locationServices";
import {
  getLocalHelperApplicationCounts,
  getLocalStartingSoonHelpers,
} from "../services/localHelperMomentumServices";
import { getIpLocation } from "../services/ipLocationServices";
import { getLocalMarketLookupKey } from "../services/localMarketLookupKey";
import {
  hasFreshLocalMarketData,
  LOCAL_MARKET_CACHE_TTL_MS,
  parseLocalMarketCache,
} from "../services/localMarketCache";
import {
  hasDisplayableLocation,
  getLocationSourceRank,
  normalizeLocationSource,
} from "../services/locationQuality";
import { trackOnce as trackOnceRaw } from "../services/track";
import {
  MEMBER_COUNT,
  REVIEW_COUNT,
  VISIT_COUNT,
} from "../services/variableServices";
import {
  buildFunnelAttribution,
  buildFunnelContext,
} from "../services/funnel/funnelSchema";
import {
  trackFunnelEvent,
  updateFunnelSessionSummary,
} from "../services/funnel/funnelEventStore";
import {
  buildQrFlyerReferralAttribution,
  clearPendingReferralAttribution,
  clearPendingReferralCode,
  isValidReferralCode,
  normalizeFlyerInstanceId,
  normalizeReferralCode as normalizeReferralStorageCode,
  QR_FLYER_REFERRAL_CHANNEL,
  QR_FLYER_SOURCE,
  UNKNOWN_QR_FLYER_INSTANCE_ID,
} from "../services/referralStorage";
import {
  SENIOR_FLYER_QR_MEDIUM,
  SENIOR_FLYER_QR_CAMPAIGN,
} from "../services/referralServices";
import {
  getCurrentSessionId,
  getOrCreateSessionIdentity,
} from "../services/session/sessionIdentity";
import { sanitizeLegacySessionActivitySummary } from "../services/session/legacySessionActivitySanitizer";
import { isCanonicalAuthUser } from "../services/authIdentity";
import { safeLocalStorage } from "../utils/safeStorage";
import { getNearbyPublicDemandBins } from "../components/preOnboarding/utils/publicDemandBins";
import { useAuth } from "./AuthProvider";
import { SessionContext, useSession } from "./SessionContext";

const OPERATIONAL_LOG_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;
const operationalLogExpiresAt = () =>
  Timestamp.fromMillis(Date.now() + OPERATIONAL_LOG_RETENTION_MS);

export { SessionContext, useSession };

const DEBUG_FLAGS = {
  FORCE_NO_LOCATION: false,
  FORCE_NO_SYSTEM: false,
  FORCE_NO_SESSION: false,
};

const HELPER_ANALYTICS_ONCE_STORAGE_KEY = "helperAnalytics:v1:once";
const RETIRED_FUNNEL_SESSION_EXPERIMENT_KEYS = new Set([
  "locations-section-v1",
  "locations-section-v2",
]);
const MAX_HELPER_ANALYTICS_HELPERS_PER_REQUEST = 100;
const HELPER_ANALYTICS_SOURCE_BY_BIN = {
  websiteTraffic: "session_provider",
  signups: "member_signup",
  newMembers: "signup_fee_payment",
  profileViews: "helper_profile",
  visitsBooked: "schedule_visit_flow",
};
const HELPER_ANALYTICS_ACTION_BY_BIN = {
  websiteTraffic: "search_results_impressed",
  signups: "account_created",
  newMembers: "membership_started",
  profileViews: "profile_opened",
  visitsBooked: "visit_booked",
};
const LOCAL_HELPER_PREFETCH_IDLE_TIMEOUT_MS = 600;
const LOCAL_HELPER_PREFETCH_FALLBACK_DELAY_MS = 100;
const LOCAL_HELPER_PREFETCH_LOOKUP_TIMEOUT_MS = 3500;
const LOCAL_MARKET_CACHE_PREFIX = "linkedLives.localMarketData.v2";
const LOCAL_MARKET_PUBLIC_DEMAND_THRESHOLD = 5;
const LOCAL_MARKET_RETRY_DELAY_MS = 350;
const EMPTY_LOCAL_HELPER_MOMENTUM = Object.freeze({
  geohash5: "",
  appliedRecently: 0,
  previousPeriodApplied: 0,
  appliedInLastTwoPeriods: 0,
});
const helperAnalyticsInFlightKeys = new Set();
const pacificDayFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Los_Angeles",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});
const QR_FLYER_SCAN_DEVICE_DAY_PREFIX = "qrFlyerScan:v2:deviceDay";

const buildQrFlyerScanDeviceDayKey = ({
  referralCode = "",
  flyerInstanceId = "",
  dayKey = "",
}) =>
  `${QR_FLYER_SCAN_DEVICE_DAY_PREFIX}:${dayKey}:${
    referralCode || "anonymous"
  }:${flyerInstanceId}`;

const buildQrFlyerScanEventKey = ({
  referralCode = "",
  flyerInstanceId = "",
  dayKey = "",
  sessionId = "",
} = {}) => {
  const subjectKey =
    String(sessionId || "")
      .trim()
      .replace(/[^a-zA-Z0-9:_-]/g, "_")
      .slice(0, 140) || "anonymous-session";
  return `qr_flyer_scan:v2:${dayKey}:${referralCode || "anonymous"}:${flyerInstanceId}:${subjectKey}`;
};

const removeQrFlyerParamsFromUrl = (
  url,
  { removeReferralCode = false } = {},
) => {
  url.searchParams.delete("q");
  url.searchParams.delete("v");
  url.searchParams.delete("h");
  url.searchParams.delete("c");
  url.searchParams.delete("f");
  if (removeReferralCode) {
    url.searchParams.delete("r");
  }
  const newUrl =
    url.pathname +
    (url.searchParams.toString() ? `?${url.searchParams.toString()}` : "") +
    url.hash;
  window.history.replaceState({}, "", newUrl);
};

const getPacificDayKey = () => {
  try {
    const parts = pacificDayFormatter.formatToParts(new Date());
    const byType = Object.fromEntries(
      parts.map((part) => [part.type, part.value]),
    );
    if (byType.year && byType.month && byType.day) {
      return `${byType.year}-${byType.month}-${byType.day}`;
    }
  } catch (_error) {
    // Analytics dedupe should never block app usage.
  }
  return new Date().toISOString().slice(0, 10);
};

const getHelperAnalyticsOnceMap = () => {
  try {
    const parsed = JSON.parse(
      safeLocalStorage.getItem(HELPER_ANALYTICS_ONCE_STORAGE_KEY) || "{}",
    );
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (_error) {
    return {};
  }
};

const setHelperAnalyticsOnceMap = (value = {}) => {
  try {
    const keys = Object.keys(value);
    if (keys.length > 2000) {
      keys.sort(
        (left, right) => Number(value[left] || 0) - Number(value[right] || 0),
      );
      keys.slice(0, keys.length - 2000).forEach((key) => delete value[key]);
    }
    safeLocalStorage.setItem(
      HELPER_ANALYTICS_ONCE_STORAGE_KEY,
      JSON.stringify(value),
    );
  } catch (_error) {
    // Ignore storage pressure/private-mode failures.
  }
};

const filterUnsentHelperAnalyticsTargets = ({
  helperIds = [],
  bin = "",
  sessionId = "",
  userId = "",
  eventKey = "",
} = {}) => {
  const dayKey = getPacificDayKey();
  const subjectKey = eventKey || userId || sessionId || "anon";
  const datePart = eventKey ? "all" : dayKey;
  const onceMap = getHelperAnalyticsOnceMap();
  const normalizedHelperIds = Array.from(
    new Set(
      (Array.isArray(helperIds) ? helperIds : [])
        .map((value) => String(value || "").trim())
        .filter(Boolean),
    ),
  ).slice(0, MAX_HELPER_ANALYTICS_HELPERS_PER_REQUEST);
  const unsentHelperIds = normalizedHelperIds.filter((helperId) => {
    const key = `${bin}|${helperId}|${datePart}|${subjectKey}`;
    return !onceMap[key] && !helperAnalyticsInFlightKeys.has(key);
  });
  const activeKeys = unsentHelperIds.map(
    (helperId) => `${bin}|${helperId}|${datePart}|${subjectKey}`,
  );

  return {
    unsentHelperIds,
    markInFlight() {
      activeKeys.forEach((key) => helperAnalyticsInFlightKeys.add(key));
    },
    clearInFlight() {
      activeKeys.forEach((key) => helperAnalyticsInFlightKeys.delete(key));
    },
    markSent() {
      const nowMs = Date.now();
      activeKeys.forEach((key) => {
        onceMap[key] = nowMs;
        helperAnalyticsInFlightKeys.delete(key);
      });
      setHelperAnalyticsOnceMap(onceMap);
    },
  };
};

const buildCrashSystemSummary = (systemData = null) => {
  if (!systemData || typeof systemData !== "object") {
    return null;
  }

  return {
    deviceCategory: systemData.deviceCategory || null,
    deviceType: systemData.deviceType || null,
    browser: systemData.browser || null,
    screenSize: systemData.screenSize || null,
    effectiveType: systemData.effectiveType || null,
    language: systemData.language || null,
    timeZone: systemData.timeZone || null,
    isTouchDevice: Boolean(systemData.isTouchDevice),
  };
};

const getCurrentRoute = () => {
  if (typeof window === "undefined") return "";
  return `${window.location.pathname || "/"}${window.location.hash || ""}`;
};

const getSessionPlatform = () => {
  if (
    typeof navigator !== "undefined" &&
    navigator.userAgentData?.mobile !== undefined
  ) {
    return navigator.userAgentData.mobile ? "mobile" : "desktop";
  }

  const userAgent =
    typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  return /Mobi|Android|iPhone|iPad|iPod/i.test(userAgent)
    ? "mobile"
    : "desktop";
};

export const setDebugFlags = (overrides = {}) =>
  Object.assign(DEBUG_FLAGS, overrides);

const readStoredLocationData = () => {
  const raw = safeLocalStorage.getItem("locationData");
  if (!raw || raw === "null" || raw === "undefined") return null;

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (!hasDisplayableLocation(parsed)) return null;
    return {
      ...parsed,
      locationSource: normalizeLocationSource(parsed, "stored"),
    };
  } catch {
    safeLocalStorage.removeItem("locationData");
    return null;
  }
};

const writeStoredLocationData = (locationData) => {
  if (!hasDisplayableLocation(locationData)) return;
  safeLocalStorage.setItem("locationData", JSON.stringify(locationData));
};

const getLocalHelperLookupKey = getLocalMarketLookupKey;

const getLocalMarketCacheKey = (lookupKey = "") =>
  lookupKey ? `${LOCAL_MARKET_CACHE_PREFIX}:${lookupKey}` : "";

const readCachedLocalMarketData = (lookupKey = "") => {
  const cacheKey = getLocalMarketCacheKey(lookupKey);
  if (!cacheKey) return null;

  try {
    const raw = safeLocalStorage.getItem(cacheKey);
    if (!raw) return null;

    const parsed = parseLocalMarketCache(raw);
    if (
      !parsed ||
      parsed.lookupKey !== lookupKey ||
      Number(parsed.expiresAt || 0) <= Date.now() ||
      !parsed.data ||
      typeof parsed.data !== "object" ||
      !hasFreshLocalMarketData(parsed.data) ||
      parsed.data.availabilityFailed
    ) {
      return null;
    }

    return parsed.data;
  } catch (_error) {
    return null;
  }
};

const writeCachedLocalMarketData = (lookupKey = "", data = {}) => {
  const cacheKey = getLocalMarketCacheKey(lookupKey);
  if (!cacheKey || !data || typeof data !== "object") return;

  try {
    safeLocalStorage.setItem(
      cacheKey,
      JSON.stringify({
        lookupKey,
        expiresAt: Date.now() + LOCAL_MARKET_CACHE_TTL_MS,
        data,
      }),
    );
  } catch (_error) {
    // Storage is best-effort; the in-memory session cache still works.
  }
};

const wait = (ms = 0) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const resolveLookupValue = async (
  promiseFactory,
  fallbackValue,
  {
    timeoutMs = LOCAL_HELPER_PREFETCH_LOOKUP_TIMEOUT_MS,
    label = "lookup",
  } = {},
) => {
  let timeoutId;
  const lookupPromise = Promise.resolve()
    .then(promiseFactory)
    .then((value) => ({ ok: true, value }))
    .catch((error) => {
      console.warn(`${label} failed.`, error);
      return { ok: false, error, value: fallbackValue };
    });

  const timeoutPromise = new Promise((resolve) => {
    timeoutId = setTimeout(() => {
      console.warn(`${label} timed out after ${timeoutMs}ms.`);
      resolve({ ok: false, timedOut: true, value: fallbackValue });
    }, timeoutMs);
  });

  try {
    return await Promise.race([lookupPromise, timeoutPromise]);
  } finally {
    clearTimeout(timeoutId);
  }
};

const resolveLookupValueWithRetry = async (
  promiseFactory,
  fallbackValue,
  options = {},
) => {
  const first = await resolveLookupValue(
    promiseFactory,
    fallbackValue,
    options,
  );
  if (first.ok) return first;

  await wait(LOCAL_MARKET_RETRY_DELAY_MS);
  return resolveLookupValue(promiseFactory, fallbackValue, {
    ...options,
    label: `${options.label || "lookup"} retry`,
  });
};

const buildEmptyLocalMarketData = (lookupKey = "") => ({
  lookupKey,
  localHelpers: {},
  localStartingSoonHelpers: {},
  localHelperMomentum: { ...EMPTY_LOCAL_HELPER_MOMENTUM },
  nearbyDemand: {
    nearbyMemberCount: 0,
    nearbyPoints: [],
  },
  fetchedAt: 0,
});

const scheduleLocalHelperPrefetch = (callback) => {
  if (typeof window === "undefined") {
    callback();
    return () => {};
  }

  if ("requestIdleCallback" in window) {
    const idleId = window.requestIdleCallback(callback, {
      timeout: LOCAL_HELPER_PREFETCH_IDLE_TIMEOUT_MS,
    });
    return () => {
      if (typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleId);
      }
    };
  }

  const timeoutId = window.setTimeout(
    callback,
    LOCAL_HELPER_PREFETCH_FALLBACK_DELAY_MS,
  );
  return () => window.clearTimeout(timeoutId);
};

const getSystemData = () => {
  const { userAgent = "unknown device", connection } = navigator;

  const isTouchDevice =
    "ontouchstart" in window || navigator.maxTouchPoints > 0;

  return {
    // Device and platform details
    deviceType: getDeviceType(userAgent, isTouchDevice).type, // Detailed type (e.g., iPhone, Mac)
    deviceCategory: getDeviceType(userAgent, isTouchDevice).category, // "mobile" or "desktop"
    isTouchDevice,
    browser: getBrowserInfo(userAgent),

    // Screen and display
    screenSize: `${window.screen.width || "unknown"}x${
      window.screen.height || "unknown"
    }`,

    // Network information
    effectiveType: connection?.effectiveType || "unknown",

    // Locale and timezone
    language: navigator.language || "unknown",
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || "unknown",

    // Preferences
    // cookiesEnabled: navigator.cookieEnabled || false,
    // doNotTrack: navigator.doNotTrack || "unknown",
  };
};

// Enhanced device type detection
const getDeviceType = (userAgent, isTouchDevice) => {
  const ua = userAgent.toLowerCase();

  // Determine device type
  if (/android/.test(ua))
    return { type: "Android Phone/Tablet", category: "mobile" };
  if (/iphone/.test(ua)) return { type: "iPhone", category: "mobile" };
  if (/ipad|macintosh/.test(ua) && isTouchDevice)
    return { type: "iPad", category: "mobile" };
  if (/macintosh/.test(ua)) return { type: "Mac", category: "desktop" };
  if (/windows/.test(ua)) return { type: "Windows PC", category: "desktop" };
  if (/linux/.test(ua)) return { type: "Linux PC", category: "desktop" };

  // General browser types
  if (/mobile/.test(ua)) return { type: "Mobile Browser", category: "mobile" };
  if (/tablet/.test(ua)) return { type: "Tablet Browser", category: "mobile" };

  return { type: "Desktop Browser", category: "desktop" };
};

// Browser detection
const getBrowserInfo = (userAgent) => {
  const match = userAgent.match(
    /(firefox|msie|trident|chrome|safari|edge|opera|opr|edg)\/?\s*(\d+)/i,
  );
  if (match && match[1]) {
    return `${match[1]} ${match[2] || "unknown"}`;
  }
  return "unknown";
};

// SessionProvider Component
export const SessionProvider = ({ children }) => {
  const { currentUser, loading: authLoading } = useAuth();
  const canonicalCurrentUser = isCanonicalAuthUser(currentUser);
  const initialSessionIdentityRef = useRef(null);
  if (!initialSessionIdentityRef.current) {
    initialSessionIdentityRef.current = getOrCreateSessionIdentity() || {
      sessionId: "",
      created: false,
      primaryStorage: "none",
    };
  }
  const initialLocationDataRef = useRef({ initialized: false, value: null });
  if (!initialLocationDataRef.current.initialized) {
    initialLocationDataRef.current = {
      initialized: true,
      value: DEBUG_FLAGS.FORCE_NO_LOCATION ? null : readStoredLocationData(),
    };
  }

  const [sessionId, setSessionId] = useState(
    initialSessionIdentityRef.current.sessionId,
  );
  const [experimentGroup, setExperimentGroup] = useState(null);
  const [locationData, setLocationData] = useState(
    initialLocationDataRef.current.value,
  );
  // we care about
  //  .city = "Anacortes"
  //  .region = "Washington"
  //  .region_code = "WA"
  //  .postal = "98221"
  //  .latitude = 48.5181
  //  .longitude = -122.6385
  // Assignment telemetry can fire on the first render, before the deferred
  // session initialization effect. Capture the device category synchronously.
  const [systemData, setSystemData] = useState(() =>
    !DEBUG_FLAGS.FORCE_NO_SYSTEM &&
    typeof window !== "undefined" &&
    typeof navigator !== "undefined"
      ? getSystemData()
      : null,
  );
  const [localHelpers, setLocalHelpers] = useState({});
  const [localStartingSoonHelpers, setLocalStartingSoonHelpers] = useState({});
  const [localHelperMomentum, setLocalHelperMomentum] = useState({
    ...EMPTY_LOCAL_HELPER_MOMENTUM,
  });
  const [loadingLocalHelperMomentum, setLoadingLocalHelperMomentum] =
    useState(true);
  const [nearbyDemandMembers, setNearbyDemandMembers] = useState(0);
  const [nearbyDemandPoints, setNearbyDemandPoints] = useState([]);
  const [nearbyDemandLoaded, setNearbyDemandLoaded] = useState(false);
  const [localMarketData, setLocalMarketData] = useState(() =>
    buildEmptyLocalMarketData(""),
  );
  const [localMarketStatus, setLocalMarketStatus] = useState({
    lookupKey: "",
    status: "idle",
    error: null,
    fetchedAt: 0,
  });

  const [utmSource, setUtmSource] = useState(null);

  const lastPageVisitLogRef = useRef({ key: "", at: 0 });
  const lastFunnelSessionSummaryRef = useRef("");
  const localHelperLookupRef = useRef({
    key: "",
    inFlightKey: "",
    attemptedKey: "",
  });
  const localMarketInFlightRef = useRef(new Map());
  const localMarketRequestRef = useRef(null);
  const helperTrafficAnalyticsRef = useRef("");

  const [memberCount, setMemberCount] = useState(MEMBER_COUNT);
  const [reviewCount, setReviewCount] = useState(REVIEW_COUNT);
  const [visitCount, setVisitCount] = useState(VISIT_COUNT);
  const [locationImages] = useState([]);

  const trackOnceEvent = async (
    event,
    { scope = "session", key = null, properties = {} } = {},
  ) => {
    const finalSessionId = getCurrentSessionId(sessionId);
    const userId = currentUser?.uid || null;
    if (!userId) return null;

    return trackOnceRaw({
      event,
      userId,
      sessionId: finalSessionId,
      scope,
      key,
      properties,
      context: {
        utmSource: utmSource || safeLocalStorage.getItem("utm_source") || null,
        experimentGroup: experimentGroup || null,
        systemData: systemData ? JSON.parse(JSON.stringify(systemData)) : null,
        locationData: locationData
          ? JSON.parse(JSON.stringify(locationData))
          : null,
      },
    });
  };

  useEffect(() => {
    if (!canonicalCurrentUser) {
      return undefined;
    }

    const fetchStats = async () => {
      // Count active members
      try {
        const memberSnap = await getCountFromServer(
          query(collection(db, "members"), where("status", "==", "active")),
        );
        setMemberCount(memberSnap.data().count);
      } catch (error) {
        console.error("Error fetching member count:", error);
      }

      // Count 5-star reviews
      try {
        const reviewSnap = await getCountFromServer(
          query(collection(db, "visits"), where("rating", "==", 5)),
        );
        setReviewCount(reviewSnap.data().count);
      } catch (error) {
        console.error("Error fetching review count:", error);
      }

      // Count confirmed visits
      try {
        const confirmedSnap = await getCountFromServer(
          query(collection(db, "visits"), where("status", "==", "confirmed")),
        );
        setVisitCount(confirmedSnap.data().count);
      } catch (error) {
        console.error("Error fetching visit count:", error);
      }
    };

    const timerId = window.setTimeout(() => {
      fetchStats();
    }, 1500);

    return () => window.clearTimeout(timerId);
  }, [canonicalCurrentUser, currentUser?.uid]);

  useEffect(() => {
    const systemSummary = buildCrashSystemSummary(systemData);

    setCrashSessionContext({
      sessionId: sessionId || null,
      experimentGroup: experimentGroup || null,
      systemSummary,
    });

    if (!sessionId && !experimentGroup && !systemSummary) {
      return;
    }

    addCrashBreadcrumb({
      category: "session",
      message: "session_context_updated",
      data: {
        hasSessionId: Boolean(sessionId),
        experimentGroup: experimentGroup || null,
      },
    });
  }, [experimentGroup, sessionId, systemData]);

  useEffect(() => {
    if (!sessionId) return;
    if (!systemData && !locationData && !utmSource) return;

    const context = buildFunnelContext({
      route: getCurrentRoute(),
      systemData,
      locationData,
    });
    const attribution = buildFunnelAttribution({
      utmSource,
      storedAttribution: readStoredAdAttribution(),
      locationData,
    });
    const fingerprint = JSON.stringify({
      sessionId,
      userId: canonicalCurrentUser ? currentUser.uid : null,
      context,
      attribution,
    });
    if (lastFunnelSessionSummaryRef.current === fingerprint) return;
    lastFunnelSessionSummaryRef.current = fingerprint;

    updateFunnelSessionSummary({
      sessionId,
      userId: canonicalCurrentUser ? currentUser.uid : null,
      context,
      attribution,
    }).catch((error) => {
      console.error("Failed to update funnel session summary:", error);
    });
  }, [canonicalCurrentUser, currentUser?.uid, locationData, sessionId, systemData, utmSource]);

  // Initialize session when the provider mounts
  useEffect(() => {
    if (authLoading) {
      return undefined;
    }

    let cancelled = false;

    const initializeSession = async () => {
      // Get session data with test flag
      const systemData = DEBUG_FLAGS.FORCE_NO_SYSTEM
        ? undefined
        : await getSystemData();
      let locationData;
      if (DEBUG_FLAGS.FORCE_NO_LOCATION) {
        locationData = undefined;
      } else {
        locationData = readStoredLocationData();
      }
      if (DEBUG_FLAGS.FORCE_NO_LOCATION) locationData = undefined;
      const sessionIdentity = DEBUG_FLAGS.FORCE_NO_SESSION
        ? { sessionId: "", created: false, primaryStorage: "none" }
        : getOrCreateSessionIdentity() || {
            sessionId: "",
            created: false,
            primaryStorage: "none",
          };
      let storedSessionId = sessionIdentity.sessionId;
      if (DEBUG_FLAGS.FORCE_NO_SESSION) storedSessionId = null;

      const params = new URLSearchParams(window.location.search);
      const paramsUtmSource = params.get("utm_source") || "";

      // Update session state variables
      if (cancelled) {
        return;
      }

      setLocationData(locationData);

      setSystemData(systemData);
      setUtmSource(paramsUtmSource);

      // Save params utm_source if they exist
      if (paramsUtmSource) {
        safeLocalStorage.setItem("utm_source", paramsUtmSource);
      }

      const group = assignExperimentGroup(storedSessionId); // uses default experiment
      setExperimentGroup(group);

      // Set sessionId once we've loaded or created it
      setSessionId(storedSessionId || "");
      if (initialSessionIdentityRef.current) {
        initialSessionIdentityRef.current.created = false;
      }

      if (!DEBUG_FLAGS.FORCE_NO_LOCATION && !locationData) {
        getIpLocation()
          .then((detectedLocation) => {
            if (cancelled || !detectedLocation) return;

            setLocationData((currentLocation) => {
              if (
                currentLocation &&
                getLocationSourceRank(currentLocation) >
                  getLocationSourceRank(detectedLocation)
              ) {
                return currentLocation;
              }

              writeStoredLocationData(detectedLocation);
              return detectedLocation;
            });
          })
          .catch((error) => {
            console.warn("IP location detection failed.", error);
          });
      }
    };

    let timeoutId = null;
    let idleId = null;

    const kickoff = () => {
      initializeSession();
    };

    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(kickoff, { timeout: 200 });
    } else {
      timeoutId = window.setTimeout(kickoff, 200);
    }

    return () => {
      cancelled = true;

      if (timeoutId) window.clearTimeout(timeoutId);
      if (idleId && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleId);
      }
    };
  }, [authLoading, currentUser?.uid]);

  const updateAnalytics = useCallback(
    async ({
      helperIds = [],
      bin,
      currentUserId = "",
      eventKey = "",
      properties = {},
    } = {}) => {
      if (window.location.hostname === "localhost") {
        return null;
      }

      let finalSessionId = getCurrentSessionId(sessionId);

      const explicitUserId =
        typeof currentUserId === "string" ? currentUserId.trim() : "";
      const authenticatedUserId = currentUser?.uid || "";
      const trackingId =
        explicitUserId || authenticatedUserId || finalSessionId;

      const platform = getSessionPlatform();

      // Filter out helperIds containing "manual"
      const sourceHelperIds = Array.isArray(helperIds) ? helperIds : [];
      const filteredHelperIds = sourceHelperIds.filter(
        (id) => typeof id === "string" && !id.toLowerCase().includes("manual"),
      );
      const dedupe = filterUnsentHelperAnalyticsTargets({
        helperIds: filteredHelperIds,
        bin,
        sessionId: finalSessionId,
        userId: explicitUserId || authenticatedUserId,
        eventKey,
      });
      if (!dedupe.unsentHelperIds.length) {
        return null;
      }
      const routePath =
        typeof window !== "undefined" && window.location
          ? window.location.pathname || "/"
          : "/";
      const telemetryProperties = {
        source: HELPER_ANALYTICS_SOURCE_BY_BIN[bin] || "unknown",
        actionType: HELPER_ANALYTICS_ACTION_BY_BIN[bin] || bin,
        sourceRoute: routePath,
        resultSetSize: filteredHelperIds.length,
        hasSearchLocation: Boolean(
          locationData?.latitude && locationData?.longitude,
        ),
        geographyState:
          locationData?.region_code ||
          locationData?.region ||
          locationData?.state ||
          "",
        ...properties,
      };

      try {
        dedupe.markInFlight();
        const result = await callCloudRunFunction(
          "maintenance-handler",
          "updateAnalytics",
          {
            currentUserId: trackingId,
            helperIds: dedupe.unsentHelperIds,
            sessionId: finalSessionId,
            platform,
            bin,
            experimentGroup,
            experimentKey: EXPERIMENT.key,
            eventKey,
            properties: telemetryProperties,
          },
          { requiresAuth: false },
        );
        dedupe.markSent();
        return result;
      } catch (error) {
        dedupe.clearInFlight();
        console.error("Failed to update analytics:", error);
        return null;
      }
    },
    [currentUser?.uid, experimentGroup, locationData, sessionId],
  );

  const applyLocalMarketData = useCallback(
    (
      nextMarketData = buildEmptyLocalMarketData(""),
      {
        lookupKey = nextMarketData?.lookupKey || "",
        status = "ready",
        error = null,
        commitToSession = true,
      } = {},
    ) => {
      if (!commitToSession) return;

      const safeData = {
        ...buildEmptyLocalMarketData(lookupKey),
        ...(nextMarketData || {}),
        lookupKey,
      };
      const nearbyDemand = safeData.nearbyDemand || {};
      const safeMomentum =
        safeData.localHelperMomentum &&
        typeof safeData.localHelperMomentum === "object"
          ? safeData.localHelperMomentum
          : EMPTY_LOCAL_HELPER_MOMENTUM;

      localHelperLookupRef.current = {
        key: status === "ready" ? lookupKey : "",
        inFlightKey: status === "loading" ? lookupKey : "",
        attemptedKey: status === "failed" ? lookupKey : "",
      };
      setLocalHelpers(safeData.localHelpers || {});
      setLocalStartingSoonHelpers(safeData.localStartingSoonHelpers || {});
      setLocalHelperMomentum({
        ...EMPTY_LOCAL_HELPER_MOMENTUM,
        ...safeMomentum,
      });
      setLoadingLocalHelperMomentum(status === "loading");
      setNearbyDemandMembers(Number(nearbyDemand?.nearbyMemberCount || 0));
      setNearbyDemandPoints(
        Array.isArray(nearbyDemand?.nearbyPoints)
          ? nearbyDemand.nearbyPoints
          : [],
      );
      setNearbyDemandLoaded(status !== "loading" && Boolean(lookupKey));
      setLocalMarketData(safeData);
      setLocalMarketStatus({
        lookupKey,
        status,
        error,
        fetchedAt: Number(safeData.fetchedAt || 0),
      });
    },
    [],
  );

  const hasLocalMarketDataToClear = Boolean(
    localMarketStatus.lookupKey ||
    localMarketStatus.status !== "idle" ||
    nearbyDemandLoaded ||
    nearbyDemandMembers > 0 ||
    nearbyDemandPoints.length > 0 ||
    Object.keys(localHelpers || {}).length > 0 ||
    Object.keys(localStartingSoonHelpers || {}).length > 0,
  );

  const ensureLocalMarketData = useCallback(
    async ({
      locationData: lookupLocationData = locationData,
      force = false,
      includeDemandPoints = true,
      pointThreshold = LOCAL_MARKET_PUBLIC_DEMAND_THRESHOLD,
      availabilityResolver = getActiveEmployeesAtLocation,
      nearbyDemandResolver = getNearbyPublicDemandBins,
      helperLookupTimeoutMs = LOCAL_HELPER_PREFETCH_LOOKUP_TIMEOUT_MS,
      usePersistentCache = availabilityResolver ===
        getActiveEmployeesAtLocation &&
        nearbyDemandResolver === getNearbyPublicDemandBins,
      commitToSession = true,
      quiet = false,
    } = {}) => {
      const lookupKey = getLocalHelperLookupKey(lookupLocationData);
      const request = { lookupKey };
      if (commitToSession) localMarketRequestRef.current = request;
      const applyCurrentMarketData = (data, options) => {
        if (!commitToSession || localMarketRequestRef.current === request) {
          applyLocalMarketData(data, options);
        }
      };
      if (!lookupKey) {
        const emptyData = buildEmptyLocalMarketData("");
        if (commitToSession && hasLocalMarketDataToClear) {
          applyCurrentMarketData(emptyData, {
            lookupKey: "",
            status: "idle",
            commitToSession,
          });
        }
        return emptyData;
      }

      if (
        !force &&
        commitToSession &&
        localMarketStatus.lookupKey === lookupKey &&
        localMarketStatus.status === "ready" &&
        hasFreshLocalMarketData(localMarketData)
      ) {
        return localMarketData;
      }

      if (!force && usePersistentCache) {
        const cachedData = readCachedLocalMarketData(lookupKey);
        if (cachedData) {
          applyCurrentMarketData(cachedData, {
            lookupKey,
            status: "ready",
            commitToSession,
          });
          return cachedData;
        }
      }

      const applyLoadingState = () => {
        if (quiet) return;
        const loadingMarketData =
          commitToSession && localMarketStatus.lookupKey === lookupKey
            ? localMarketData
            : buildEmptyLocalMarketData(lookupKey);
        applyLocalMarketData(loadingMarketData, {
          lookupKey,
          status: "loading",
          commitToSession,
        });
      };

      const flightKey = [
        lookupKey,
        usePersistentCache ? "default" : "custom",
        includeDemandPoints ? "points" : "counts",
        commitToSession ? "commit" : "read",
      ].join("|");
      if (!force && localMarketInFlightRef.current.has(flightKey)) {
        // Joining the same work must retain its right to publish to the session.
        if (commitToSession) {
          localMarketRequestRef.current = localMarketInFlightRef.current.get(flightKey)?.request;
        }
        applyLoadingState();
        return localMarketInFlightRef.current.get(flightKey).promise;
      }

      applyLoadingState();

      const lat = Number(lookupLocationData?.latitude);
      const lng = Number(lookupLocationData?.longitude);
      const loadPromise = (async () => {
        const [
          helpersResult,
          nearbyDemandResult,
          startingSoonResult,
          applicationCountsResult,
        ] = await Promise.all([
          resolveLookupValueWithRetry(
            () =>
              availabilityResolver(lat, lng, {
                locationData: lookupLocationData,
                enableCityViewportLookup: false,
                helperLookupTimeoutMs,
              }),
            {},
            {
              timeoutMs: helperLookupTimeoutMs,
              label: "local helper availability lookup",
            },
          ),
          resolveLookupValueWithRetry(
            () =>
              nearbyDemandResolver({
                latitude: lat,
                longitude: lng,
                includePoints: includeDemandPoints,
                pointThreshold,
              }),
            {
              nearbyMemberCount: 0,
              nearbyPoints: [],
            },
            {
              timeoutMs: helperLookupTimeoutMs,
              label: "local public demand lookup",
            },
          ),
          resolveLookupValueWithRetry(
            () =>
              getLocalStartingSoonHelpers({
                lat,
                lng,
              }),
            {},
            {
              timeoutMs: helperLookupTimeoutMs,
              label: "local starting-soon helper lookup",
            },
          ),
          resolveLookupValueWithRetry(
            () =>
              getLocalHelperApplicationCounts({
                lat,
                lng,
              }),
            { ...EMPTY_LOCAL_HELPER_MOMENTUM },
            {
              timeoutMs: helperLookupTimeoutMs,
              label: "local helper application lookup",
            },
          ),
        ]);

        const nextData = {
          lookupKey,
          localHelpers: helpersResult.value || {},
          localStartingSoonHelpers: startingSoonResult.value || {},
          localHelperMomentum: {
            ...EMPTY_LOCAL_HELPER_MOMENTUM,
            ...(applicationCountsResult.value || {}),
          },
          nearbyDemand: {
            nearbyMemberCount: Number(
              nearbyDemandResult.value?.nearbyMemberCount || 0,
            ),
            nearbyPoints: Array.isArray(nearbyDemandResult.value?.nearbyPoints)
              ? nearbyDemandResult.value.nearbyPoints
              : [],
          },
          fetchedAt: Date.now(),
          availabilityFailed: !helpersResult.ok,
          demandFailed: !nearbyDemandResult.ok,
          startingSoonFailed: !startingSoonResult.ok,
          helperMomentumFailed: !applicationCountsResult.ok,
        };
        const isReady =
          helpersResult.ok &&
          nearbyDemandResult.ok &&
          startingSoonResult.ok &&
          applicationCountsResult.ok;

        if (isReady && usePersistentCache &&
            localMarketInFlightRef.current.get(flightKey)?.request === request) {
          writeCachedLocalMarketData(lookupKey, nextData);
        }

        applyCurrentMarketData(nextData, {
          lookupKey,
          status: isReady ? "ready" : "failed",
          error: isReady ? null : "local_market_lookup_failed",
          commitToSession,
        });

        return nextData;
      })()
        .catch((error) => {
          console.error("Error fetching local market data:", error);
          const failedData = buildEmptyLocalMarketData(lookupKey);
          applyCurrentMarketData(failedData, {
            lookupKey,
            status: "failed",
            error,
            commitToSession,
          });
          return {
            ...failedData,
            availabilityFailed: true,
            demandFailed: true,
          };
        })
        .finally(() => {
          if (localMarketInFlightRef.current.get(flightKey)?.request === request) {
            localMarketInFlightRef.current.delete(flightKey);
          }
        });

      localMarketInFlightRef.current.set(flightKey, { request, promise: loadPromise });
      return loadPromise;
    },
    [
      applyLocalMarketData,
      hasLocalMarketDataToClear,
      localMarketData,
      localMarketStatus.lookupKey,
      localMarketStatus.status,
      locationData,
    ],
  );

  const retryLocalMarketData = useCallback(
    (options = {}) =>
      ensureLocalMarketData({
        ...options,
        force: true,
      }),
    [ensureLocalMarketData],
  );

  useEffect(() => {
    if (localMarketRequestRef.current?.lookupKey !== getLocalHelperLookupKey(locationData)) {
      localMarketRequestRef.current = null;
    }
  }, [locationData]);

  useEffect(() => () => { localMarketRequestRef.current = null; }, []);

  useEffect(() => {
    const lookupKey = getLocalHelperLookupKey(locationData);
    if (!lookupKey) {
      localHelperLookupRef.current = {
        key: "",
        inFlightKey: "",
        attemptedKey: "",
      };

      if (hasLocalMarketDataToClear) {
        applyLocalMarketData(buildEmptyLocalMarketData(""), {
          lookupKey: "",
          status: "idle",
        });
      }
      return undefined;
    }

    if (
      localHelperLookupRef.current.key === lookupKey ||
      localHelperLookupRef.current.inFlightKey === lookupKey ||
      localHelperLookupRef.current.attemptedKey === lookupKey
    ) {
      return undefined;
    }

    let cancelled = false;
    const cancelScheduledPrefetch = scheduleLocalHelperPrefetch(() => {
      if (cancelled) return;
      ensureLocalMarketData({ locationData, quiet: true });
    });

    return () => {
      cancelled = true;
      cancelScheduledPrefetch();
    };
  }, [
    applyLocalMarketData,
    ensureLocalMarketData,
    hasLocalMarketDataToClear,
    locationData,
  ]);

  // Log website traffic once helper data is available.
  useEffect(() => {
    if (!(currentUser?.uid || sessionId) || !systemData || !locationData) {
      return;
    }

    const lookupKey = getLocalHelperLookupKey(locationData);
    if (!lookupKey || localHelperLookupRef.current.key !== lookupKey) return;

    const helpersArr = Object.keys(localHelpers || {});
    if (!helpersArr.length) return;

    const analyticsKey = `${sessionId || currentUser?.uid || ""}:${lookupKey}:${helpersArr
      .slice()
      .sort()
      .join(",")}`;
    if (helperTrafficAnalyticsRef.current === analyticsKey) return;
    helperTrafficAnalyticsRef.current = analyticsKey;

    updateAnalytics({
      helperIds: helpersArr,
      bin: "websiteTraffic",
    });
  }, [
    currentUser?.uid,
    locationData,
    localHelpers,
    sessionId,
    systemData,
    updateAnalytics,
  ]);

  // --- QR SCAN COUNT (daily device/code scoped) ---
  useEffect(() => {
    if (!sessionId) return;

    const url = new URL(window.location.href);
    if (!url.searchParams.has("v")) return;
    const flyerInstanceId = normalizeFlyerInstanceId(
      url.searchParams.get("f") || "",
    );
    const normalizedReferralCode = normalizeReferralStorageCode(
      url.searchParams.get("r") || "",
    );
    const referralCode = isValidReferralCode(normalizedReferralCode)
      ? normalizedReferralCode
      : "";
    const isSeniorFlyerQrV2 =
      Boolean(referralCode) &&
      /^[a-z0-9]{1,40}$/i.test(String(url.searchParams.get("v") || "").trim());
    if (!isSeniorFlyerQrV2) {
      removeQrFlyerParamsFromUrl(url);
      return;
    }
    const metricFlyerInstanceId =
      flyerInstanceId || UNKNOWN_QR_FLYER_INSTANCE_ID;
    const dayKey = getPacificDayKey();
    const deviceDayKey = buildQrFlyerScanDeviceDayKey({
      referralCode,
      flyerInstanceId: metricFlyerInstanceId,
      dayKey,
    });
    const existingDeviceDayMarker = safeLocalStorage.getItem(deviceDayKey);
    if (
      existingDeviceDayMarker === "true" ||
      existingDeviceDayMarker === "self"
    ) {
      if (existingDeviceDayMarker === "self") {
        clearPendingReferralCode();
        clearPendingReferralAttribution();
        removeQrFlyerParamsFromUrl(url, { removeReferralCode: true });
        return;
      }
      if (!referralCode) {
        removeQrFlyerParamsFromUrl(url);
      }
      return;
    }

    (async () => {
      try {
        if (currentUser?.uid && referralCode) {
          let isOwnSignedInHelperQr = false;
          try {
            const codeSnap = await getDoc(
              doc(db, "referralCodes", referralCode),
            );
            const codeData = codeSnap.exists() ? codeSnap.data() || {} : {};
            isOwnSignedInHelperQr =
              String(codeData.ownerUid || "").trim() === currentUser.uid &&
              String(codeData.ownerType || "")
                .trim()
                .toLowerCase() === "helper";
          } catch (error) {
            console.warn("Unable to resolve QR referral owner:", error);
          }
          if (isOwnSignedInHelperQr) {
            safeLocalStorage.setItem(deviceDayKey, "self");
            clearPendingReferralCode();
            clearPendingReferralAttribution();
            removeQrFlyerParamsFromUrl(url, { removeReferralCode: true });
            return;
          }
        }

        const attribution = buildQrFlyerReferralAttribution({
          referralCode,
          flyerVersion: url.searchParams.get("v") || "",
          flyerInstanceId,
        });
        await updateFunnelSessionSummary({
          sessionId,
          context: buildFunnelContext({
            route: getCurrentRoute(),
            systemData,
            locationData,
          }),
          attribution,
          properties: {
            source: QR_FLYER_SOURCE,
            channel: QR_FLYER_REFERRAL_CHANNEL,
            hasReferralCode: Boolean(referralCode),
            referralCode,
            flyerVersion: attribution.flyerVersion,
            flyerInstanceId: metricFlyerInstanceId,
            hasFlyerInstanceId: Boolean(attribution.flyerInstanceId),
          },
        });
        const scanEventKey = buildQrFlyerScanEventKey({
          referralCode,
          flyerInstanceId: metricFlyerInstanceId,
          dayKey,
          sessionId,
        });
        await trackFunnelEvent(
          {
            eventName: "qr_flyer_scan_detected",
            eventType: "view",
            flow: "landing",
            surface: "senior_flyer_qr",
            sessionId,
            dedupeKey: scanEventKey,
            context: buildFunnelContext({
              route: getCurrentRoute(),
              systemData,
              locationData,
            }),
            attribution,
            properties: {
              source: QR_FLYER_SOURCE,
              medium: SENIOR_FLYER_QR_MEDIUM,
              campaign: SENIOR_FLYER_QR_CAMPAIGN,
              channel: QR_FLYER_REFERRAL_CHANNEL,
              referralSource: attribution.referralSource,
              hasReferralCode: Boolean(referralCode),
              referralCode,
              flyerVersion: attribution.flyerVersion,
              flyerInstanceId: metricFlyerInstanceId,
              hasFlyerInstanceId: Boolean(attribution.flyerInstanceId),
            },
          },
          {
            immediate: true,
            once: true,
            key: scanEventKey,
            qualifyingActivity: false,
          },
        );
        safeLocalStorage.setItem(deviceDayKey, "true");
      } catch (err) {
        console.error("Failed to record QR scan:", err);
      } finally {
        if (!referralCode) {
          removeQrFlyerParamsFromUrl(url);
        }
      }
    })();
  }, [currentUser?.uid, locationData, sessionId, systemData]);

  // custom useEffect hook that handles application of URL referral codes
  useCaptureReferralFromUrl({ sessionId });

  // Helper function to generate the summary
  const generateSummary = (
    initiatorName,
    receiverName,
    interactionType,
    madeContact,
  ) => {
    // Format the interaction types into natural language
    const interactionSummary = formatInteractionTypes(interactionType);

    // Use ternary operator to determine whether contact was made
    return `${initiatorName} ${interactionSummary} ${receiverName} ${
      madeContact ? "and had a conversation." : "with no response."
    }`;
  };

  const updateLocationDataFromGoogle = async (newData) => {
    setLocationData((prev) => {
      const updated = normalizeManualLocationData(newData, prev || {});

      writeStoredLocationData(updated);
      return updated;
    });

    // refresh images for new city/state
    // if (newData.latitude && newData.longitude) {
    //   const imgs = await fetchLocationPhotos(
    //     `${newData?.city} ${newData?.state}`
    //   );
    //   setLocationImages(imgs);
    // }

    // Fetch and store minimum wage for new location
    // const newWage = await getMinimumWage(newData.city, newData.state);
    // safeLocalStorage.setItem("minimumWage", newWage);
    // setMinimumWage(newWage);
  };

  // Helper function to convert interaction types into natural language
  const formatInteractionTypes = (interactionType) => {
    const formattedTypes = interactionType.map((type) => {
      switch (type) {
        case "call":
          return "called";
        case "text":
          return "texted";
        case "email":
          return "emailed";
        case "voicemail":
          return "left a voicemail";
        case "in-person":
          return "was in-person with";
        case "automated email":
          return "sent an automated email to";
        case "automated text":
          return "sent an automated text to";
        default:
          return type;
      }
    });

    // Join the interaction types into a natural language sentence
    if (formattedTypes.length === 0) {
      return ""; // Return an empty string if no types are present
    } else if (formattedTypes.length === 1) {
      return formattedTypes[0]; // Return the single item if there's only one
    } else if (formattedTypes.length === 2) {
      return formattedTypes.join(" and "); // Join with "and" for exactly two items
    } else {
      // For three or more items
      return (
        formattedTypes.slice(0, -1).join(", ") +
        ", and " +
        formattedTypes[formattedTypes.length - 1]
      );
    }
  };

  const logExperimentExposure = async (experimentKey) => {
    if (!sessionId || !experimentKey || !experimentGroup) return;
    if (RETIRED_FUNNEL_SESSION_EXPERIMENT_KEYS.has(experimentKey)) return;

    await updateFunnelSessionSummary({
      sessionId,
      userId: canonicalCurrentUser ? currentUser.uid : null,
      properties: {
        experimentKeys: [experimentKey],
        variantKeys: [experimentGroup],
        experimentPairs: [`${experimentKey}:${experimentGroup}`],
      },
    });
  };

  // Function to create a communication log
  const createCommunicationLog = async ({
    madeContact, // bool TRUE or FALSE
    collectionName = "", // e.g., "membersPrivate" or "visits"
    idsArr, // Array of related IDs, e.g., ["memberId456", "employee123", "visit789"]
    description, // Brief summary of the communication
    interactionType, // ["Call", "Email", "Text", ...]
    initiator, // { type: "employee" || "member" || "system", id: "employee123", displayName: "Alex R.", avatarUrl: "" }
    receiver, // { type: "employee" || "member" || "system", id: "member123", displayName: "John Doe", avatarUrl: "" }
    tags = [], // Optional tags for the log (e.g., ["mailbox_full", "call_failed"])
    sentiment = 3, // Default sentiment rating is neutral
    interest = 3, // Default interest rating is neutral
    priority = 1, // Default priority level
    flags = [], // Optional flag, e.g., ["payment", "urgent"]
    communicationDate,
    followUpDate,
  }) => {
    if (!canonicalCurrentUser) return null;
    try {
      // Generate the summary based on the initiator, receiver, and interaction
      const summary = generateSummary(
        initiator.displayName,
        receiver.displayName,
        interactionType,
        madeContact,
      );

      // Core log structure
      const log = {
        // Identifiers
        collection: collectionName || "",
        idsArr, // Array of related IDs
        accessLevel: "private", // "public", "private", or "admin"
        logType: "communication", // Type is "communication" for communication logs
        flags, // Optional flags
        systemData,
        locationData,
        // Core log fields
        summary, // Brief summary of the communication
        created: Timestamp.now(), // Created timestamp
        expiresAt: operationalLogExpiresAt(),
        edited: null, // Edited timestamp (same as created for new logs)

        // Communication-specific fields (communication details)
        communication: {
          interactionType, // e.g., ["Call", "Email"]
          description, // A description of what was discussed or what occurred
          communicationDate: Timestamp.fromDate(communicationDate.toDate()), // Convert to Firestore Timestamp
          // followUpDate: Timestamp.fromDate(followUpDate.toDate()), // Convert to Firestore Timestamp
          tags, // Tags like "mailbox_full", "call_failed", etc.
          // sentiment, // Sentiment rating (1-5)
          // interest, // Interest rating (1-5)
          // priority, // Priority level
          initiator, // Details of the person/system who initiated the interaction
          receiver, // Details of the person/system who received the interaction
          madeContact,
        },
      };

      // Add the log to the Firestore logs collection
      const logRef = collection(db, "logs"); // Referencing the logs collection in Firestore
      await addDoc(logRef, log); // Adding the log document to Firestore
    } catch (error) {
      console.error("Error creating communication log:", error);
    }
  };

  // Function to create a basic log
  const createLog = async ({
    collectionName = "", // e.g., "membersPrivate" or "visits"
    idsArr = [], // Array of related IDs, e.g., ["memberId456", "employee123", "visit789"]
    summary, // Brief summary of the log
    flags = [], // Optional flag, e.g., ["payment", "urgent"]
    accessLevel = "private",
    logType = "basic",
  }) => {
    if (!canonicalCurrentUser) return null;
    try {
      // Core log structure
      const log = {
        // Identifiers
        collection: collectionName || "",
        idsArr, // Array of related IDs
        accessLevel, // "public", "private", or "admin"
        logType, // Type is "communication" for communication logs
        flags, // Optional flags
        systemData: JSON.parse(JSON.stringify(systemData ?? null)),
        locationData: JSON.parse(JSON.stringify(locationData ?? null)),

        // Core log fields
        summary, // Single line log summary
        created: Timestamp.now(), // Created timestamp
        expiresAt: operationalLogExpiresAt(),
        edited: null, // Edited timestamp (same as created for new logs)
      };

      // Add the log to the Firestore logs collection
      const logRef = collection(db, "logs"); // Referencing the logs collection in Firestore
      await addDoc(logRef, log); // Adding the log document to Firestore
    } catch (error) {
      console.error("Error creating communication log:", error);
    }
  };

  // Function to create a communication log
  const createVisitLog = async ({
    collectionName = "", // e.g., "membersPrivate" or "visits"
    idsArr = [], // Array of related IDs, e.g., ["memberId456", "employee123", "visit789"]
    summary = "", // Brief summary of the log
    flags = [], // Optional flag, e.g., ["payment", "urgent"]
    accessLevel = "private",
    logType = "visit",
    visitId = null,
  }) => {
    if (!canonicalCurrentUser) return null;
    try {
      // Core log structure
      const log = {
        // Identifiers
        collection: collectionName || "",
        idsArr, // Array of related IDs
        accessLevel, // "public", "private", or "admin"
        logType, // Type is "communication" for communication logs
        flags, // Optional flags

        // Core log fields
        summary, // Single line log summary
        created: Timestamp.now(), // Created timestamp
        expiresAt: operationalLogExpiresAt(),
        edited: null, // Edited timestamp (same as created for new logs)
        systemData,
        locationData,

        visit: {
          visitId,
        },
      };

      // Add the log to the Firestore logs collection
      const logRef = collection(db, "logs"); // Referencing the logs collection in Firestore
      await addDoc(logRef, log); // Adding the log document to Firestore
    } catch (error) {
      console.error("Error creating communication log:", error);
    }
  };

  // Helper functions now inside the provider with early returns
  const logPageVisit = async (path) => {
    // The support bot response is in a useMemo which gives a stale sessionId
    let finalSessionId = getCurrentSessionId(sessionId);

    if (!currentUser?.uid && !finalSessionId) return;

    const userId = currentUser?.uid || null;
    const fallbackId = finalSessionId || null;

    const idsArr = [userId, fallbackId].filter(Boolean); // removes null/undefined
    const now = Date.now();
    const visitKey = `${idsArr.join("|")}:${path || ""}`;

    if (
      lastPageVisitLogRef.current.key === visitKey &&
      now - lastPageVisitLogRef.current.at < 30000
    ) {
      return;
    }

    lastPageVisitLogRef.current = { key: visitKey, at: now };

    if (!canonicalCurrentUser) return;

    createLog({
      idsArr,
      summary: `Viewed the page ${path}`,
      logType: "activity",
    });
  };

  const logSessionActivity = async (value) => {
    if (!canonicalCurrentUser) return;

    const safeSummary = sanitizeLegacySessionActivitySummary(value);
    if (!safeSummary) return;

    // The support bot response is in a useMemo which gives a stale sessionId
    let finalSessionId = getCurrentSessionId(sessionId);

    if (!currentUser?.uid && !finalSessionId) return;

    const userId = currentUser?.uid || null;
    const fallbackId = finalSessionId || null;

    const idsArr = [userId, fallbackId].filter(Boolean); // removes null/undefined

    createLog({
      idsArr,
      summary: safeSummary,
      logType: "activity",
    });
  };

  const logChatBotActivity = async (messageSummary) => {
    if (!canonicalCurrentUser) return;

    const safeSummary = sanitizeLegacySessionActivitySummary(messageSummary);
    if (!safeSummary) return;

    // The support bot response is in a useMemo which gives a stale sessionId
    let finalSessionId = getCurrentSessionId(sessionId);

    if (!currentUser?.uid && !finalSessionId) return;

    const userId = currentUser?.uid || null;
    const fallbackId = finalSessionId || null;

    const idsArr = [userId, fallbackId].filter(Boolean); // removes null/undefined

    // Create log entry
    await createLog({
      idsArr,
      summary: safeSummary,
      logType: "activity",
      flags: ["chatBot"],
    });
  };

  return (
    <SessionContext.Provider
      value={{
        sessionId,
        experimentGroup,
        systemData,
        locationData,
        updateLocationDataFromGoogle,
        utmSource,
        localHelpers,
        setLocalHelpers,
        localStartingSoonHelpers,
        setLocalStartingSoonHelpers,
        localHelperMomentum,
        setLocalHelperMomentum,
        loadingLocalHelperMomentum,
        setLoadingLocalHelperMomentum,
        nearbyDemandMembers,
        nearbyDemandPoints,
        nearbyDemandLoaded,
        localMarketData,
        localMarketStatus,
        ensureLocalMarketData,
        retryLocalMarketData,
        logPageVisit,
        logSessionActivity,
        logChatBotActivity,
        trackOnceEvent,
        createCommunicationLog,
        updateAnalytics,
        createLog,
        createVisitLog,
        logExperimentExposure,

        locationImages, // ✅ now separate from locationData

        // Global counts
        memberCount,
        reviewCount,
        visitCount,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};
